
import { distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { Filter, FilterSet, FilterGroup, FilterConfig, FilterSetService } from '../../../services/filter-set.service';
import { Component, Input, HostListener, ViewChild, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FeatureDetectionService, DeviceInfo } from '../../../services/feature-detection.service';

@Component({
	selector: 'bb-filters-controls',
	templateUrl: './bb-filters-controls.component.html',
	styleUrls: ['./bb-filters-controls.component.scss']
})

export class BbFiltersControlsComponent implements OnInit, OnDestroy {

	@Input() lightBackground = false;
	@ViewChild('filterSearchEl') filterSearchEl: ElementRef;

	public addingFilter = false;
	public addFilterSearchResults: FilterConfig[];
	public filterSet: FilterSet;
	public filterConfig: FilterConfig[] = [];

	private query: string;
	private activeSearchResult: FilterConfig;

	private searchFormControl: UntypedFormControl;
	private el: HTMLElement;

	private filterSetChangedSubscription: Subscription;
	private filterConfigChangedSubscription: Subscription;
	private isMobileSafari: boolean;

	constructor(
		private _eref: ElementRef,
		private filterSetService: FilterSetService,
		private featureDetectionService: FeatureDetectionService
	) {

		this.searchFormControl = new UntypedFormControl();
		this.filterSetService = filterSetService;
		this.el = this._eref.nativeElement;

		this.featureDetectionService.deviceInfoObservable.subscribe((deviceInfo: DeviceInfo) => {
			this.isMobileSafari = deviceInfo.isMobileSafari;
		});
	}

	@HostListener('document:keyup', ['$event'])
	onKeyUp(ev: KeyboardEvent) {
		if (ev.key === 'Escape') {
			this.addingFilter = false;
		} else if (ev.key === 'Enter') {
			setTimeout(() => {
				this.searchFilters(this.query);
			});
		}
	}

	@HostListener('document:click', ['$event'])
	onClick(ev: Event) {
		setTimeout(() => {
			this.addingFilter = false;
		});
	}

	ngOnInit() {
		this.filterSet = this.filterSetService.getFilterSet();
		this.filterConfig = this.filterSetService.getFilterConfig();

		this.filterSetChangedSubscription = this.filterSetService.filterSetChangedObservable.subscribe(newFilterSet => {
			this.filterSet = newFilterSet;
		});

		this.filterConfigChangedSubscription = this.filterSetService.filterConfigChangedObservable.subscribe(newFilterConfig => {
			this.filterConfig = newFilterConfig;
		});

		this.searchFormControl.valueChanges.pipe(
			debounceTime(400),
			distinctUntilChanged())
			.subscribe(newValue => {
				this.searchFilters(newValue);
			});

		if (this.filterSearchEl) {
			this.filterSearchEl.nativeElement.addEventListener('keyup', (evt: KeyboardEvent) => {
				if (evt.key === 'ArrowUp') {
					this.moveActiveSearchResult('up');
				}

				if (evt.key === 'ArrowDown') {
					this.moveActiveSearchResult('down');
				}

				if (evt.key === 'Enter') {
					setTimeout(() => {
						this.addFilter(this.activeSearchResult);
					});
				}
			});
		}
	}

	ngOnDestroy() {
		this.filterSetChangedSubscription.unsubscribe();
		this.filterConfigChangedSubscription.unsubscribe();
	}


	getLabelForProperty(property: string): string {
		if (this.filterConfig) {
			const config = this.filterConfig.find((filterConfig: FilterConfig) => filterConfig.property === property);

			return (config ? config.label : '');
		}
	}

	addFilter(filterConfig?: FilterConfig, event?: MouseEvent) {
		this.filterSetService.addFilterGroupFromFilterConfig(filterConfig);
		this.activeSearchResult = null;
		this.addingFilter = false;
	}

	getFilterForProperty(property: string): Filter {
		let foundFilter;

		this.filterSet.forEach((filterGroup: FilterGroup) => {
			filterGroup.filters.forEach((filter: Filter) => {
				if (filter.field === property) {
					foundFilter = filter;
				}
			});
		});

		return foundFilter;
	}

	editFilter(filter: Filter, event?: MouseEvent) {
		if (event) {
			event.stopPropagation();
		}

		this.filterSetService.setActiveFilter(filter);
		this.filterSetService.moveFilterToStart(filter);

		this.addingFilter = false;
	}

	moveActiveSearchResult(direction: string = 'down') {
		for (let i = 0; i < this.addFilterSearchResults.length; i++) {

			const searchResult = this.addFilterSearchResults[i];

			if (direction === 'down' &&
				(!this.activeSearchResult || (this.activeSearchResult === this.addFilterSearchResults[this.addFilterSearchResults.length - 1]))) {

				// select first
				this.setActiveSearchResult(this.addFilterSearchResults[0]);
				return;
			}

			if (direction === 'up' && (!this.activeSearchResult || this.activeSearchResult === this.addFilterSearchResults[0])) {
				// select last
				this.setActiveSearchResult(this.addFilterSearchResults[this.addFilterSearchResults.length - 1]);
				return;
			}

			if (direction === 'down' && searchResult === this.activeSearchResult && typeof this.addFilterSearchResults[i + 1] !== 'undefined') {
				this.setActiveSearchResult(this.activeSearchResult = this.addFilterSearchResults[i + 1]);
				break;
			}

			if (direction === 'up' && searchResult === this.activeSearchResult && i > 0) {
				this.setActiveSearchResult(this.addFilterSearchResults[i - 1]);
				break;
			}
		}
	}

	setActiveSearchResult(filter: FilterConfig) {
		this.activeSearchResult = filter;

		// scroll active search result into view
		const activeListItem = this.el.querySelector('li.active');

		const overflowDiv = activeListItem.parentNode as HTMLElement;
		const overflowDivTop = overflowDiv.scrollTop;

		const overflowDivBottom = overflowDivTop + overflowDiv.getBoundingClientRect().height;
		const listItemTop = activeListItem.getBoundingClientRect().top + overflowDiv.scrollTop -
			(activeListItem.parentNode as HTMLElement).getBoundingClientRect().top;
		const listItemBottom = listItemTop + activeListItem.getBoundingClientRect().height;

		if (listItemBottom > overflowDivBottom || listItemTop < overflowDivTop) {
			overflowDiv.scrollTop = listItemTop;
		}
	}

	searchFilters(search: string) {
		if (!search) {
			if (this.filterConfig) {
				this.addFilterSearchResults = [... this.filterConfig];
			}
			return;
		}

		const addFilterSearchResults: FilterConfig[] = [];

		this.filterConfig.forEach(filterConfig => {
			if (filterConfig.label.toUpperCase().indexOf(search.toUpperCase()) > -1) {
				addFilterSearchResults.push(filterConfig);
			}
		});

		this.addFilterSearchResults = addFilterSearchResults;
		this.activeSearchResult = null;
	}

	mouseOverEditFilter(filter: Filter, event?: MouseEvent) {
		if (this.isMobileSafari) {
			this.editFilter(filter, event);
		}
	}

	mouseOverAddFilter(filterConfig: FilterConfig, event?: MouseEvent) {
		this.activeSearchResult = filterConfig;
		if (this.isMobileSafari) {
			this.addFilter(filterConfig, event);
		}
	}

	startAddFilter(event: MouseEvent) {
		event.stopPropagation();

		this.addingFilter = true;

		setTimeout(() => {
			if (this.filterSearchEl) {
				this.filterSearchEl.nativeElement.focus();
			}
		});
	}
}
